import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { setupAxiosInterceptors } from '@/lib/axios-interceptors';
import { useAuthStore } from '@/stores/authStore';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { EmailVerificationPage } from '@/pages/auth/EmailVerificationPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { PlansPage } from '@/pages/PlansPage';
import { Loading } from '@/components/ui/Loading';
import { ToastContainer } from '@/components/ui/Toast';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Role } from '@/types/rbac';
import { Resource, Scope } from '@/types/rbac';

// Lazy load pages for code splitting
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const DashboardPage2 = lazy(() => import('@/pages/DashboardPage2').then(m => ({ default: m.DashboardPage2 })));
const FlowsPage = lazy(() => import('@/pages/FlowsPage').then(m => ({ default: m.FlowsPage })));
const FlowBuilderPage = lazy(() => import('@/pages/FlowBuilderPage'));
const FlowBuilderV2Page = lazy(() => import('@/pages/FlowBuilderV2Page'));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const AgentsPage = lazy(() => import('@/pages/AgentsPage').then(m => ({ default: m.AgentsPage })));
const DataAnalyticsPage = lazy(() => import('@/pages/DataAnalyticsPage').then(m => ({ default: m.DataAnalyticsPage })));
const ManagementPage = lazy(() => import('@/pages/ManagementPage').then(m => ({ default: m.ManagementPage })));
const ReportsPage = lazy(() => import('@/pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const ExecutionNodeReportPage = lazy(() => import('@/pages/ExecutionNodeReportPage').then(m => ({ default: m.ExecutionNodeReportPage })));
const CredentialsPage = lazy(() => import('@/pages/CredentialsPage').then(m => ({ default: m.CredentialsPage })));
const TemplatesPage = lazy(() => import('@/pages/TemplatesPage').then(m => ({ default: m.TemplatesPage })));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const PlanManagementPage = lazy(() => import('@/pages/PlanManagementPage').then(m => ({ default: m.PlanManagementPage })));
const I18nTestPage = lazy(() => import('@/pages/I18nTestPage').then(m => ({ default: m.I18nTestPage })));
const ModalScrollTestPage = lazy(() => import('@/pages/ModalScrollTestPage').then(m => ({ default: m.ModalScrollTestPage })));
const TestContinueOnErrorPage = lazy(() => import('@/pages/TestContinueOnErrorPage').then(m => ({ default: m.TestContinueOnErrorPage })));
const TestPlanLimitPage = lazy(() => import('@/pages/TestPlanLimitPage').then(m => ({ default: m.TestPlanLimitPage })));
const IntegrationErrorsPage = lazy(() => import('@/pages/IntegrationErrorsPage').then(m => ({ default: m.IntegrationErrorsPage })));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage').then(m => ({ default: m.default })));
const TermsOfServicePage = lazy(() => import('@/pages/TermsOfServicePage').then(m => ({ default: m.default })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const GoogleOAuthCallbackPage = lazy(() => import('@/pages/GoogleOAuthCallbackPage'));

// RBAC pages
const AdminPanelPage = lazy(() => import('@/pages/AdminPanelPage').then(m => ({ default: m.AdminPanelPage })));
const MembersPage = lazy(() => import('@/pages/MembersPage').then(m => ({ default: m.MembersPage })));
const AuditLogsPage = lazy(() => import('@/pages/AuditLogsPage').then(m => ({ default: m.AuditLogsPage })));
const BusinessEntitiesPage = lazy(() => import('@/pages/BusinessEntitiesPage').then(m => ({ default: m.BusinessEntitiesPage })));
const DynamicEntityScreenPage = lazy(() => import('@/pages/DynamicEntityScreenPage'));
const AccessDeniedPage = lazy(() => import('@/pages/AccessDeniedPage').then(m => ({ default: m.AccessDeniedPage })));
const AcceptInvitePage = lazy(() => import('@/pages/auth/AcceptInvitePage'));

// Ticket pages
const TicketListPage = lazy(() => import('@/pages/TicketListPage').then(m => ({ default: m.TicketListPage })));
const TicketDetailPage = lazy(() => import('@/pages/TicketDetailPage').then(m => ({ default: m.TicketDetailPage })));
const AdminTicketListPage = lazy(() => import('@/pages/AdminTicketListPage').then(m => ({ default: m.AdminTicketListPage })));
const AdminTicketDetailPage = lazy(() => import('@/pages/AdminTicketDetailPage').then(m => ({ default: m.AdminTicketDetailPage })));

function App() {
  const { initializeAuth, isInitialized } = useAuthStore();

  useEffect(() => {
    // Initialize auth from localStorage
    initializeAuth();
    
    // Setup axios interceptors
    setupAxiosInterceptors();
  }, [initializeAuth]);

  // Aguardar a inicialização da autenticação antes de renderizar as rotas
  if (!isInitialized) {
    return <Loading fullScreen text="Carregando..." />;
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ToastContainer />
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/termos-de-servico" element={<TermsOfServicePage />} />
            <Route path="/credentials/google/callback" element={<GoogleOAuthCallbackPage />} />
            <Route path="/access-denied" element={<AccessDeniedPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            
            {/* Protected routes with layout */}
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage2 />} />
              <Route path="/dashboard-v1" element={<DashboardPage />} />
              <Route path="/flows" element={
                <ProtectedRoute resource={Resource.Flows} scope={Scope.LIST}>
                  <FlowsPage />
                </ProtectedRoute>
              } />
              <Route path="/flows/new" element={
                <ProtectedRoute resource={Resource.Flows} scope={Scope.WRITE}>
                  <FlowBuilderV2Page />
                </ProtectedRoute>
              } />
              <Route path="/flows/:flowId/edit" element={
                <ProtectedRoute resource={Resource.Flows} scope={Scope.EDIT}>
                  <FlowBuilderV2Page />
                </ProtectedRoute>
              } />
              <Route path="/agents" element={
                <ProtectedRoute resource={Resource.Agents} scope={Scope.LIST}>
                  <AgentsPage />
                </ProtectedRoute>
              } />
              <Route path="/data-analytics" element={
                <ProtectedRoute resource={Resource.Flows} scope={Scope.LIST}>
                  <DataAnalyticsPage />
                </ProtectedRoute>
              } />
              
              {/* Old Flow Builder V1 Routes (mantidas para fallback) */}
              <Route path="/flows-v1/new" element={
                <ProtectedRoute resource={Resource.Flows} scope={Scope.WRITE}>
                  <FlowBuilderPage />
                </ProtectedRoute>
              } />
              <Route path="/flows-v1/:flowId/edit" element={
                <ProtectedRoute resource={Resource.Flows} scope={Scope.EDIT}>
                  <FlowBuilderPage />
                </ProtectedRoute>
              } />
              
              <Route path="/projects" element={
                <ProtectedRoute resource={Resource.Projects} scope={Scope.LIST}>
                  <ProjectsPage />
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute resource={Resource.Executions} scope={Scope.LIST}>
                  <ReportsPage />
                </ProtectedRoute>
              } />
              <Route path="/reports/execution/:executionId/nodes" element={
                <ProtectedRoute resource={Resource.Executions} scope={Scope.READ}>
                  <ExecutionNodeReportPage />
                </ProtectedRoute>
              } />
              <Route path="/credentials" element={
                <ProtectedRoute resource={Resource.ApiCredentials} scope={Scope.LIST}>
                  <CredentialsPage />
                </ProtectedRoute>
              } />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/plan" element={<PlanManagementPage />} />
              <Route path="/i18n-test" element={<I18nTestPage />} />
              <Route path="/modal-scroll-test" element={<ModalScrollTestPage />} />
              <Route path="/test-continue-on-error" element={<TestContinueOnErrorPage />} />
              <Route path="/test-plan-limit" element={<TestPlanLimitPage />} />
              <Route path="/integration-errors" element={<IntegrationErrorsPage />} />

              {/* Gestão (any authenticated member) */}
              <Route path="/gestao" element={<ManagementPage />} />

              {/* Tickets - User Portal (any authenticated member) */}
              <Route path="/tickets" element={<TicketListPage />} />
              <Route path="/tickets/:ticketId" element={<TicketDetailPage />} />
            </Route>

            {/* Admin routes - requires ADMIN role */}
            <Route
              element={
                <ProtectedRoute requiredRole={Role.ADMIN}>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/admin" element={<AdminPanelPage />} />
              <Route path="/admin/tickets" element={<AdminTicketListPage />} />
              <Route path="/admin/tickets/:ticketId" element={<AdminTicketDetailPage />} />
            </Route>

            {/* Manager routes - requires GERENTE role and Members scope */}
            <Route
              element={
                <ProtectedRoute requiredRole={Role.GERENTE} resource={Resource.Members} scope={Scope.LIST}>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/members" element={<MembersPage />} />
              <Route path="/audit-logs" element={<AuditLogsPage />} />
            </Route>

            {/* Business Entities - requires GERENTE role */}
            <Route
              element={
                <ProtectedRoute requiredRole={Role.GERENTE}>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/business-entities" element={<BusinessEntitiesPage />} />
              <Route path="/entity-screen" element={<DynamicEntityScreenPage />} />
            </Route>
            
            {/* Redirects and 404 */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
